import { useEffect } from 'react';
import { create } from 'zustand';
import { PRESENCE_BEAT_MS, type FriendPresence, type Invite } from '@il-colpo/protocol';
import { getAccountStore, useAccount } from '../account/accountStore.ts';
import { useFlag } from '../meta/liveStore.ts';
import { onlineAvailable } from '../online/onlineConnection.ts';
import { fetchPresence } from './gameServerApi.ts';
import type { AppNotification, SocialOverview } from './types.ts';

/**
 * What the page knows about the player's people: the friends and requests, who of them is here,
 * the invitations that arrived, and how many notifications are unread. It is read from the account
 * service and the game server while the player is signed in with an account (not a guest) and the
 * friends flag is on (`useSocialSync`, mounted once in the layout), and kept for the screens.
 */

const OVERVIEW_FRESH_MS = 60_000;
const NOTIFICATIONS_FRESH_MS = 60_000;
/** The least time between two calls to the game server (it refuses a burst of more than eight in ten seconds). */
const PRESENCE_MIN_MS = 4_000;

/** The call that was put off because the last one was too near, if there is one. */
let pendingPresence: ReturnType<typeof setTimeout> | null = null;

interface SocialState {
  overview: SocialOverview | null;
  /** `failed` when the friends could not be read (the screen offers to try again). */
  overviewStatus: 'idle' | 'loading' | 'ready' | 'failed';
  presence: Record<string, FriendPresence>;
  /** The invitations still to be answered. */
  invites: Invite[];
  /** The invitations the player has answered or put away: they are not shown again. */
  dismissed: string[];
  notifications: AppNotification[];
  overviewAt: number;
  notificationsAt: number;
  presenceAt: number;

  refreshOverview: (force?: boolean) => Promise<void>;
  syncPresence: () => Promise<void>;
  refreshNotifications: (force?: boolean) => Promise<void>;
  dismissInvite: (id: string) => void;
  markNotificationsRead: () => Promise<void>;
  reset: () => void;
}

const EMPTY = {
  overview: null,
  overviewStatus: 'idle' as const,
  presence: {},
  invites: [],
  notifications: [],
  overviewAt: 0,
  notificationsAt: 0,
  presenceAt: 0,
};

export const useSocialStore = create<SocialState>((set, get) => ({
  ...EMPTY,
  dismissed: [],

  refreshOverview: async (force = false) => {
    const { overviewAt, overviewStatus } = get();
    if (overviewStatus === 'loading' || (!force && Date.now() - overviewAt < OVERVIEW_FRESH_MS)) {
      return;
    }
    const client = getAccountStore().getState().client;
    if (!client) return;
    if (get().overview === null) set({ overviewStatus: 'loading' });
    const r = await client.social().overview();
    if (r.ok) set({ overview: r.value, overviewStatus: 'ready', overviewAt: Date.now() });
    else
      set({
        overviewStatus: get().overview ? 'ready' : 'failed',
        overviewAt: Date.now() - OVERVIEW_FRESH_MS + 10_000,
      });
  },

  syncPresence: async () => {
    // Called too soon after the last time (the layout and a screen both ask when a page opens): once,
    // a moment later, with whatever the friends are by then.
    const wait = PRESENCE_MIN_MS - (Date.now() - get().presenceAt);
    if (wait > 0) {
      pendingPresence ??= setTimeout(() => {
        pendingPresence = null;
        void get().syncPresence();
      }, wait);
      return;
    }
    set({ presenceAt: Date.now() });
    const friends = get().overview?.friends ?? [];
    const answer = await fetchPresence(friends.map((f) => f.userId));
    if (!answer) return;
    const dismissed = new Set(get().dismissed);
    set({
      presence: answer.friends,
      invites: answer.invites.filter((i) => !dismissed.has(i.id)),
    });
  },

  refreshNotifications: async (force = false) => {
    if (!force && Date.now() - get().notificationsAt < NOTIFICATIONS_FRESH_MS) return;
    const client = getAccountStore().getState().client;
    if (!client) return;
    set({ notificationsAt: Date.now() });
    const r = await client.social().notifications();
    if (r.ok) set({ notifications: r.value });
  },

  dismissInvite: (id) => {
    set((s) => ({
      dismissed: [...s.dismissed.slice(-49), id],
      invites: s.invites.filter((i) => i.id !== id),
    }));
  },

  markNotificationsRead: async () => {
    const unread = get().notifications.filter((n) => !n.read);
    if (unread.length === 0) return;
    const client = getAccountStore().getState().client;
    if (!client) return;
    const r = await client.social().markRead(unread.map((n) => n.id));
    if (r.ok) set((s) => ({ notifications: s.notifications.map((n) => ({ ...n, read: true })) }));
  },

  reset: () => {
    if (pendingPresence !== null) clearTimeout(pendingPresence);
    pendingPresence = null;
    set({ ...EMPTY });
  },
}));

/** How many things wait for the player: requests to answer, and notifications not yet read. */
export function useSocialBadge(): number {
  return useSocialStore(
    (s) =>
      (s.overview?.incoming.length ?? 0) +
      s.notifications.filter((n) => !n.read && n.kind === 'moderation').length,
  );
}

/**
 * Keeps the social state fresh while the player is signed in with an account: the friends and the
 * notifications from the account service, and, calling the game server every twenty seconds, who is
 * here and who has invited them (which also says to the friends that this player is here).
 */
export function useSocialSync(): void {
  const signedIn = useAccount(
    (s) => s.snapshot.status === 'signedIn' && !s.snapshot.user?.anonymous,
  );
  const client = useAccount((s) => s.client);
  const enabled = useFlag('friends');
  useEffect(() => {
    if (!signedIn || !client || !enabled) {
      useSocialStore.getState().reset();
      return;
    }
    let stopped = false;
    const tick = async (): Promise<void> => {
      if (stopped || document.hidden) return;
      const store = useSocialStore.getState();
      await store.refreshOverview();
      if (onlineAvailable()) await useSocialStore.getState().syncPresence();
      await useSocialStore.getState().refreshNotifications();
    };
    void tick();
    const id = setInterval(() => {
      void tick();
    }, PRESENCE_BEAT_MS);
    return () => {
      stopped = true;
      clearInterval(id);
    };
  }, [signedIn, client, enabled]);
}
